import { TFunction } from 'react-i18next'

import type { TagRef, TermRef } from '../types'
import { formatLabel } from './formatLabel'

/**
 * The i18n namespace holding the UA Insights *vocabulary* (motivations, story
 * patterns, tag dimensions, beats, genres) as opposed to `ua-insights`, which
 * holds the UI chrome. Entries are keyed by the taxonomy item's `code`.
 */
export const TAXONOMY_NS = 'ua-insights-taxonomy'

/**
 * Lookups sourced from `GET /meta`, which the feature cannot boot without. Used
 * to name references that arrive as a bare code or id (per-second intensity
 * maps keyed by motivation code, tags carrying only a `dimensionId`).
 */
export interface TaxonomySource {
  motivationByCode: (code: string) => TermRef | undefined
  dimensionById: (id: string) => TermRef | undefined
  dimensionByCode: (code: string) => TermRef | undefined
}

/**
 * Normalise a code or label to the snake_case form the locale files use:
 * lowercase, `&` spelled out, other separators collapsed to single
 * underscores. Lets a genre code like `match-3` meet its `match_3` entry and a
 * beat label like "Build & Expand" meet `build_and_expand`.
 */
const normalise = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

/**
 * Display name for a taxonomy item: the translation keyed by its `code` when
 * one exists, otherwise the API-provided `name`. The API name always wins over
 * nothing, so a rename or a new item reaches the UI without a frontend release.
 */
const resolveLabel = (t: TFunction, section: string, code: string, fallback: string): string => {
  const translated = t(`${section}.${normalise(code)}`, { ns: TAXONOMY_NS, defaultValue: '' })
  return translated || fallback
}

/** A taxonomy reference or a bare `code` from a code-keyed map. */
type TermInput = TermRef | string

export interface UATaxonomyLabels {
  /** Motivation reference, or a motivation `code` from a code-keyed map → localised label. */
  motivationLabel: (motivation: TermInput) => string
  /** Story pattern or sub-pattern reference → localised label. */
  patternLabel: (pattern: TermRef) => string
  /** Genre reference → localised label. */
  genreLabel: (genre: TermRef) => string
  /**
   * Dimension reference, a `dimensionId` (from a {@link TagRef}) or a
   * dimension `code` (from `SecondData.observational`) → localised label.
   */
  dimensionLabel: (dimension: TermInput) => string
  /** Dimension `code` for a `dimensionId`, for code-keyed group ordering. */
  dimensionCode: (dimensionId: string) => string | undefined
  /**
   * Narrative beat label ("Hook", "Gameplay Demo") → localised label. Beats
   * arrive as English display labels, so the label itself is the lookup alias.
   */
  beatLabel: (beat: string) => string
  /**
   * Creative tag reference, or a bare tag name from `SecondData.observational`
   * → display label. Tags are a long tail of free-text phrases, so the name is
   * title-cased unless the locale file carries an override for the code.
   */
  tagLabel: (tag: TagRef | string) => string
}

/**
 * Build the label resolvers. Split from the hook so it can be unit-tested
 * without a React tree and reused by non-component code paths.
 */
export const createTaxonomyLabels = (t: TFunction, source: TaxonomySource): UATaxonomyLabels => {
  const resolveDimension = (input: TermInput): TermRef | undefined =>
    typeof input === 'string' ? source.dimensionById(input) ?? source.dimensionByCode(input) : input

  return {
    motivationLabel: (motivation) => {
      const ref = typeof motivation === 'string' ? source.motivationByCode(motivation) : motivation
      const code = ref?.code ?? (motivation as string)
      return resolveLabel(t, 'motivation', code, ref?.name ?? formatLabel(code))
    },
    patternLabel: (pattern) => resolveLabel(t, 'pattern', pattern.code, pattern.name),
    genreLabel: (genre) => resolveLabel(t, 'genre', genre.code, genre.name),
    dimensionLabel: (dimension) => {
      const ref = resolveDimension(dimension)
      if (!ref) return typeof dimension === 'string' ? formatLabel(dimension) : ''
      return resolveLabel(t, 'dimension', ref.code, ref.name)
    },
    dimensionCode: (dimensionId) => source.dimensionById(dimensionId)?.code,
    beatLabel: (beat) => resolveLabel(t, 'beat', beat, beat),
    tagLabel: (tag) => {
      if (typeof tag === 'string') return resolveLabel(t, 'tag', tag, formatLabel(tag))
      return resolveLabel(t, 'tag', tag.code, formatLabel(tag.name))
    },
  }
}
